// compute(): the whole verification of the connection for the current control values (1:1 port of the HTML).

import { currentAlphaElem1, currentAlphaElem2, currentFa, currentFi1, currentFi2, currentFn, currentFv, fmtN } from "./angles";
import { cltOuterForceGrainAngle, cltRhokWideFace, cltWideFhK, etaFhKCltNarrow, tryCurrentClt } from "./clt";
import { pInt, type EngineCtx } from "./ctx";
import { TIMBER_TIPO_MAP_REVERSE } from "./data";
import { currentScrewRecord, predrillRequirement, rondelleCompatibiliList } from "./eligibility";
import {
  elem1Trig,
  element1Lines,
  element2Bounds,
  generateScrewGrid,
  isPointAdmissible,
  passiGrigliaObliqua,
  spreadGridParams,
  type GridPoint,
} from "./grid";
import { etaFhK, PANEL_EXCLUDED_CLASSI, PANEL_MIN_T, panelFhK, panelKindOf, panelKmod } from "./materials";
import {
  ec5KserNail,
  etaKserLvl,
  faxCRkPerVite,
  faxRkVite,
  isKserLvlFamily,
  nefShearForElement,
  splittingF90Rd,
  splittingF90Rk,
  steelTimberSingleShear,
  timberTimberDoubleShear,
  timberTimberSingleShear,
  type JohansenResult,
} from "./resistance";
import { computeSpacingAt, currentDiametro, etaSpacingClt } from "./spacing";
import type { CalcState, ThreadDouble, ThreadFull } from "./state";

export function compute(ctx: EngineCtx): CalcState {
  const v = ctx.v;
  const theta = v.thetaNum;
  const alpha = theta <= 90 ? theta : 180 - theta;
  const d = currentDiametro(ctx);
  const materiale1Raw = v.materiale1;
  const steelRaw3 = materiale1Raw === "acciaio3",
    panelRaw3 = materiale1Raw === "pannello3";
  // "two sides" geometry (central timber, members 1 and 3 identical at the sides, screws from both sides ×2)
  const steel3 = steelRaw3 || panelRaw3;
  // panel-timber double shear: same geometry and physics as legno3d with element 1 (central) = panel
  const panelRawD = materiale1Raw === "pannello3d";
  const materiale1 = steelRaw3 ? "acciaio" : panelRaw3 ? "pannello" : panelRawD ? "legno3d" : materiale1Raw;
  // panel-timber: same physics as timber-timber (EC5 §8.2.2) with panel embedment/distances/kmod
  const panel1 = materiale1Raw === "pannello" || panelRaw3 || panelRawD;

  const { b1, d1, b2, d2 } = v;
  const t1 = v.spessore1,
    rhok1 = v.rhok1;
  const t2 = v.spessore2,
    rhok2 = v.rhok2;
  const { a4t1, a4c1, a3t1, a4t2, a4c2, a3t2, a3c2 } = v;
  const rows = Math.max(1, pInt(v.rows));
  const cols = Math.max(1, pInt(v.cols));
  const s11Min = v.s11,
    s12Min = v.s12; // minimum steps (fields s1/s2); the effective ones come after the spreading
  const centroIn: [number, number] = [v.centroOrizz || 0, v.centroVert || 0];
  const sfalsaViti = v.sfalsaViti;
  // THREE MEMBERS: head on element 2 (and 3), tip in the central element 1, two shear planes. 'legno3' = two single
  // shears (screws from both sides); 'legno3d' = DOUBLE SHEAR (one screw through the three members, EC5 eq. 8.7).
  const threeS = materiale1 === "legno3",
    threeD = materiale1 === "legno3d",
    three = threeS || threeD;
  const twoSides = threeS || steel3;
  const tHead = threeS ? t2 : threeD ? t2 + t1 : t1,
    tPoint = threeS ? t1 : t2;
  const nPiani = three || steel3 ? 2 : 1;

  const fi1 = currentFi1(ctx),
    fi2 = currentFi2(ctx);
  // ε1/ε2 (angle for fh,1,k/fh,2,k): screw axis / in-plane grain, automatic = 90° (the screw is always perpendicular
  // to the joint plane). CLT wide: ε = angle between force and outer-layer grain (automatic) or the entered value.
  const clt1 = tryCurrentClt(ctx, 1);
  const clt2 = tryCurrentClt(ctx, 2);
  const eps1Auto = v.eps1Auto;
  const alphaElemento1 =
    clt1 && clt1.latoInserimento === "wide" && materiale1 !== "acciaio"
      ? eps1Auto
        ? cltOuterForceGrainAngle(ctx, 1, clt1)
        : v.eps1
      : eps1Auto
        ? 90
        : v.eps1;
  const eps2Auto = v.eps2Auto;
  const alphaElemento2 =
    clt2 && clt2.latoInserimento === "wide"
      ? eps2Auto
        ? cltOuterForceGrainAngle(ctx, 2, clt2)
        : v.eps2
      : eps2Auto
        ? 90
        : v.eps2;

  // crossDominant (grid axis 2 perpendicular to element 1): recomputed from sp1/sp2 regardless of automatic distances
  const sp1ForGrid = clt1 ? etaSpacingClt(clt1.latoInserimento, d) : computeSpacingAt(ctx, currentAlphaElem1(ctx) - fi1, 1);
  const sp2ForGrid = clt2 ? etaSpacingClt(clt2.latoInserimento, d) : computeSpacingAt(ctx, currentAlphaElem2(ctx) - fi2, 2);
  const { crossDominant } = passiGrigliaObliqua(sp1ForGrid, sp2ForGrid, theta, materiale1 === "acciaio", fi1, fi2);

  const predrilled = v.etaForo === "predrilled";
  // fh2k: CLT formulas (ETA-11/0030) override the generic ones (wide: ρk = 110% of the minimum layer density)
  const fh2k = clt2
    ? clt2.latoInserimento === "wide"
      ? cltWideFhK(d, cltRhokWideFace(clt2), alphaElemento2, predrilled)
      : etaFhKCltNarrow(d)
    : etaFhK(d, rhok2, alphaElemento2, predrilled);
  // My,k from the screw DB [Nmm]; NaN (not null) when missing so that the Johansen modes d/e/f drop out of the minimum
  const screwRec = currentScrewRecord(ctx);
  const myKFromDb = screwRec ? screwRec.resistenza.MyK_Nmm : null;
  const myRk = typeof myKFromDb === "number" ? myKFromDb : NaN;

  // Washer: its height h is "consumed" before the screw enters element 1 → screwLEff = L − h replaces L everywhere
  // (Johansen embedment, lef, length rule); its outer diameter D2 becomes d_h in the head pull-through check.
  const rondelleCompat = rondelleCompatibiliList(screwRec);
  const rondellaSelCodice = v.useRondella ? v.rondellaCodice : null;
  const rondellaInfo =
    rondellaSelCodice && rondelleCompat.includes(rondellaSelCodice) ? (ctx.cat.WASHER_DB[rondellaSelCodice] ?? null) : null;
  const rondellaH = rondellaInfo ? rondellaInfo.h : 0;
  const screwL = screwRec ? screwRec.geometria_mm.L : null;
  const screwLEff = typeof screwL === "number" && Number.isFinite(screwL) ? screwL - rondellaH : screwL;

  // Fax,Rk (rope effect): tip side = element 2 (element 1 with legno3), lef limited to LG1.
  const substrate2 = clt2 ? "CLT Softwood" : TIMBER_TIPO_MAP_REVERSE[v.mat2Tipo];
  const rhok2ForFax = clt2 ? (clt2.rhok as number) : rhok2;
  const faxRk = threeS
    ? faxRkVite(
        screwRec,
        d,
        tHead,
        clt1 ? (clt1.rhok as number) : rhok1,
        clt1 ? "CLT Softwood" : TIMBER_TIPO_MAP_REVERSE[v.mat1Tipo],
        predrilled,
        screwLEff,
      ) // tip in element 1
    : faxRkVite(screwRec, d, tHead, rhok2ForFax, substrate2, predrilled, screwLEff); // double shear: tip in element 3 (data of 2) after t2 + t1

  // K_ser per shear plane (SLS): ETA LVL branch or EC5 Table 7.1 (ρm = √(ρmean,1·ρmean,2); steel: ρmean,2 and ×2;
  // panel: √(ρk,1·ρmean,2)).
  const rhomean1 = materiale1 === "acciaio" ? null : v.rhomean1;
  const rhomean2 = v.rhomean2;
  const substrate1 = materiale1 === "acciaio" ? null : clt1 ? "CLT Softwood" : TIMBER_TIPO_MAP_REVERSE[v.mat1Tipo];
  const kserLvlSubstrateElem = substrate2 === "LVL Softwood" ? 2 : substrate1 === "LVL Softwood" ? 1 : null;
  const kserLvlRhoMean = kserLvlSubstrateElem === 2 ? rhomean2 : kserLvlSubstrateElem === 1 ? rhomean1 : null;
  const useKserLvl =
    isKserLvlFamily(screwRec && screwRec.family) &&
    kserLvlSubstrateElem != null &&
    d >= 5 &&
    d <= 10 &&
    Number.isFinite(kserLvlRhoMean);
  let kser: number | null = null,
    kserBranch: "lvl" | "ec5nail" | null = null,
    kserRhoM: number | null = null;
  if (useKserLvl) {
    kser = etaKserLvl(d, kserLvlRhoMean);
    kserBranch = "lvl";
    kserRhoM = kserLvlRhoMean;
  } else {
    const rhoMGenerico =
      materiale1 === "acciaio"
        ? rhomean2
        : panel1
          ? Number.isFinite(rhok1) && Number.isFinite(rhomean2)
            ? Math.sqrt(rhok1 * rhomean2)
            : null
          : Number.isFinite(rhomean1) && Number.isFinite(rhomean2)
            ? Math.sqrt((rhomean1 as number) * rhomean2)
            : null;
    if (typeof rhoMGenerico === "number" && Number.isFinite(rhoMGenerico)) {
      // EC5 §7.1(3): steel-to-timber Kser may be multiplied by 2.0 (not for the ETA LVL branch)
      const kserSteelFactor = materiale1 === "acciaio" ? 2.0 : 1.0;
      kser = (ec5KserNail(d, rhoMGenerico, predrilled) as number) * kserSteelFactor;
      kserBranch = "ec5nail";
      kserRhoM = rhoMGenerico;
    }
  }

  // Fax,ε,Rk (head pull-through, ETA-11/0030), timber-timber only ("in steel-to-timber connections the head
  // pull-through capacity may be disregarded"): it limits the rope effect and the withdrawal checks (faxRkEf).
  const faxCRk =
    screwRec && materiale1 !== "acciaio"
      ? three
        ? faxCRkPerVite(
            screwRec,
            clt2 ? (clt2.rhok as number) : rhok2,
            clt2 ? "CLT Softwood" : TIMBER_TIPO_MAP_REVERSE[v.mat2Tipo],
            t2,
            alphaElemento2,
            rondellaInfo ? { dhRondella: rondellaInfo.d2 } : undefined,
          ) // head on element 2 (or 3)
        : faxCRkPerVite(
            screwRec,
            clt1 ? (clt1.rhok as number) : rhok1,
            clt1 ? "CLT Softwood" : TIMBER_TIPO_MAP_REVERSE[v.mat1Tipo],
            t1,
            alphaElemento1,
            rondellaInfo ? { dhRondella: rondellaInfo.d2 } : undefined,
          )
      : null;

  const lines1 = element1Lines(theta, b1, d1, a4t1, a4c1, a3t1, v.continuo1, b2);
  const bounds2 = element2Bounds(b2, d2, a4t2, a4c2, a3t2, a3c2, theta, v.continuo2, b1);
  // spreading in the admissible zone (only with automatic distances + switch): d/2 reserved for staggered rows, d
  // for staggered sides
  const spreadOn = !!(v.spreadViti && v.autoSpacing);
  const spreadMargin = (sfalsaViti ? d / 2 : 0) + (twoSides && v.sfalsaDueLati ? d : 0);
  const sp = spreadOn
    ? spreadGridParams(theta, rows, cols, s11Min, s12Min, centroIn[0], centroIn[1], lines1, bounds2, crossDominant, spreadMargin)
    : null;
  const s11 = sp ? sp.s11 : s11Min,
    s12 = sp ? sp.s12 : s12Min;
  const centroOrizz = sp ? sp.offsetX : centroIn[0],
    centroVert = sp ? sp.offsetY : centroIn[1];
  const spreadActive = !!(sp && sp.spread);
  const grid: GridPoint[] = generateScrewGrid(theta, rows, cols, s11, s12, centroOrizz, centroVert, sfalsaViti, d, crossDominant).map(
    (p) => ({ ...p, ...isPointAdmissible(p.x, p.y, lines1, bounds2, ctx.t) }),
  );
  // TWO SIDES: if the tips of the two sides would overlap in the central member (L−h > t_head + t_tip/2) and
  // «Sfalsa di 1d le viti dei due lati» is on, side 3 is shifted by 1·d: −x by default, else the first of +x, +y,
  // −y keeping more screws admissible. Side 3 screws must respect the same minimum distances (gridB.ok).
  let gridB: GridPoint[] | null = null,
    stagger = false,
    staggerDir: string | null = null;
  const tipOverlap =
    twoSides &&
    typeof screwLEff === "number" &&
    Number.isFinite(screwLEff) &&
    Number.isFinite(d) &&
    screwLEff > tHead + tPoint / 2 + 1e-9;
  const staggerOpt = !!v.sfalsaDueLati;
  if (twoSides) {
    stagger = tipOverlap && staggerOpt;
    const { c: cT, s: sT } = elem1Trig(theta);
    const mk = (dx: number, dy: number): GridPoint[] =>
      grid.map((p) => {
        const x = p.x + dx,
          y = p.y + dy;
        return { ...p, x, y, u: -x * sT + y * cT, v: x * cT + y * sT, ...isPointAdmissible(x, y, lines1, bounds2, ctx.t) };
      });
    if (!stagger) gridB = grid.map((p) => ({ ...p }));
    else {
      let best: { name: string; g: GridPoint[]; nOk: number } | null = null;
      const dirs: [string, number, number][] = [
        ["-x", -d, 0],
        ["+x", d, 0],
        ["+y", 0, d],
        ["-y", 0, -d],
      ];
      for (const [name, dx, dy] of dirs) {
        const g = mk(dx, dy),
          nOk = g.filter((q) => q.ok).length;
        if (!best || nOk > best.nOk) best = { name, g, nOk };
      }
      gridB = best ? best.g : null;
      staggerDir = best ? best.name : null;
    }
  }

  // n_ef (axial) — ETA-11/0030: n_ef = max(n^0.9; 0.9·n), n = all admissible screws (verified against MY PROJECT:
  // n=4 → 3.60). Staggered rows (EC5 Table 8.1 note 8): no reduction.
  const viteAmmissibili = grid.filter((p) => p.ok).length;
  const viteTotali = grid.length;
  const nef = sfalsaViti ? viteAmmissibili : Math.max(Math.pow(viteAmmissibili, 0.9), 0.9 * viteAmmissibili);

  // n_ef in SHEAR (EC5 8.17): "n" = screws IN ONE ROW parallel to the load: element 1 rows of `rows` screws repeated
  // `cols` times (step s11); element 2 rows of `cols` screws repeated `rows` times (step s12).
  const angleGrainElem1 = currentAlphaElem1(ctx) - fi1;
  // panel-timber: n_ef = n_ef,0 without the angle interpolation (MY PROJECT reference reports 2026-10-13)
  const angleGrainElem2 = panel1 ? 0 : currentAlphaElem2(ctx) - fi2;
  const nefShearElem2PerRiga = nefShearForElement(cols, s12, d, predrilled, sfalsaViti, angleGrainElem2, materiale1 === "acciaio");
  const nefShearElem2 = nefShearElem2PerRiga != null ? rows * nefShearElem2PerRiga : null;
  const nefShearElem1PerRiga =
    materiale1 === "acciaio" || panel1 ? null : nefShearForElement(rows, s11, d, predrilled, sfalsaViti, angleGrainElem1, false);
  const nefShearElem1 = nefShearElem1PerRiga != null ? cols * nefShearElem1PerRiga : null;
  // the row with MORE screws aligned along the grain governs (element 1 if rows ≥ cols); steel/panel: element 2
  const nefShearGovernElem: 1 | 2 = materiale1 === "acciaio" || panel1 ? 2 : rows >= cols ? 1 : 2;
  const nefShear =
    nefShearGovernElem === 1 ? (nefShearElem1 ?? nefShearElem2 ?? nef) : (nefShearElem2 ?? nefShearElem1 ?? nef);

  // Fax,Rk,ef = min(thread withdrawal, head pull-through): limits the rope effect AND the withdrawal checks.
  // DOUBLE SHEAR (user request): min(max(F1; Fhead); F2; F3), F1/F2/F3 = withdrawal of the thread portion (last LG1
  // from the tip) in the head / central / tip member; members without thread (lef = 0) do not limit.
  let threadD: ThreadDouble | null = null;
  if (threeD && screwRec && typeof screwLEff === "number" && Number.isFinite(screwLEff)) {
    const tl = t2,
      tc = t1;
    const LG1 = screwRec.geometria_mm.LG1 || screwRec.geometria_mm.L,
      th0 = Math.max(0, screwLEff - LG1);
    const ovl = (a0: number, a1: number) => Math.max(0, Math.min(a1, screwLEff) - Math.max(a0, th0));
    threadD = {
      F1: faxRkVite(screwRec, d, 0, rhok2ForFax, substrate2, predrilled, ovl(0, tl)),
      F2: faxRkVite(screwRec, d, 0, clt1 ? (clt1.rhok as number) : rhok1, substrate1, predrilled, ovl(tl, tl + tc)),
      F3: faxRkVite(screwRec, d, 0, rhok2ForFax, substrate2, predrilled, ovl(tl + tc, screwLEff)),
      Fhead: faxCRk != null ? faxCRk : Infinity,
    };
  }
  const axialD = threadD
    ? Math.min(Math.max(threadD.F1, threadD.Fhead), threadD.F2 > 0 ? threadD.F2 : Infinity, threadD.F3 > 0 ? threadD.F3 : Infinity)
    : null;
  // FULL THREAD screws in single shear (user request 2026-10-14): min(max(F1; Fhead); F2; ftens,k), F1 = withdrawal
  // of the thread inside the head-side member, F2 = tip side (faxRk). Not for steel nor double shear.
  let threadFull: ThreadFull | null = null;
  if (screwRec && screwRec.threadType === "full" && !threeD && materiale1 !== "acciaio" && typeof screwLEff === "number" && Number.isFinite(screwLEff)) {
    const LG1f = screwRec.geometria_mm.LG1 || screwRec.geometria_mm.L,
      th0f = Math.max(0, screwLEff - LG1f);
    const lefHead = Math.max(0, Math.min(tHead, screwLEff) - th0f);
    const hRho = threeS ? rhok2ForFax : clt1 ? (clt1.rhok as number) : rhok1,
      hSub = threeS ? substrate2 : substrate1;
    const F1f = faxRkVite(screwRec, d, 0, hRho, hSub, predrilled, lefHead);
    const Fheadf = faxCRk != null ? faxCRk : 0;
    const headLim = Math.max(F1f, Fheadf);
    const ftensN = Number.isFinite(screwRec.resistenza.ftensK_kN) ? screwRec.resistenza.ftensK_kN * 1000 : Infinity;
    threadFull = {
      F1: F1f,
      Fhead: Fheadf,
      F2: faxRk,
      ftens: ftensN,
      lefHead,
      headLim,
      value: Math.min(headLim > 0 ? headLim : Infinity, faxRk, ftensN),
    };
  }
  const faxRkEf =
    typeof axialD === "number" && Number.isFinite(axialD)
      ? axialD
      : threadFull
        ? threadFull.value
        : faxCRk != null
          ? Math.min(faxRk, faxCRk)
          : faxRk;

  // Fax/4 redistributed on the effective number of screws (axial n_ef): Fax,Rk,ef·(nef/viteAmmissibili), the
  // Johansen functions divide by 4 internally.
  const faxRkPerJohansen = viteAmmissibili > 0 ? faxRkEf * (nef / viteAmmissibili) : 0;

  // Embedment of the tip member for Johansen = REAL depth reached by the screw, L − h − t_head (raw t if no screw).
  const t2Johansen = typeof screwLEff === "number" && Number.isFinite(screwLEff) ? screwLEff - tHead : tPoint;

  // DOUBLE SHEAR — check PLANE BY PLANE (user request): plane [1/2] (element 2 with 1) with t1 = thickness of
  // element 2, plane [2/3] (element 1 with 3) with t1 = penetration in element 3; t2 = central member for both. Each
  // plane has its own rope effect: Fax,[1/2] = min(max(F1; Fhead); F2 + F3), Fax,[2/3] = min(max(F1; Fhead) + F2; F3).
  // The plane with the lower Fv,Rk governs.
  const doubleShearByPlane = (fh1kv: number): JohansenResult => {
    const tl = t2,
      tc = t1;
    const pen3 = Number.isFinite(t2Johansen) ? t2Johansen : tPoint;
    const th = threadD || { F1: 0, F2: 0, F3: 0, Fhead: faxCRk != null ? faxCRk : Infinity };
    const { F1, F2, F3, Fhead } = th;
    const FaxA = Math.min(Math.max(F1, Fhead), F2 + F3);
    const FaxB = Math.min(Math.max(F1, Fhead) + F2, F3);
    const sc = viteAmmissibili > 0 ? nef / viteAmmissibili : 0;
    const mk = (label: string, t1p: number, FaxP: number) => {
      const r = timberTimberDoubleShear(d, t1p, tc, fh2k, fh1kv, myRk, FaxP * sc);
      r.label = label;
      r.FaxPlane = FaxP;
      return r;
    };
    const rA = mk("[1/2]", tl, FaxA),
      rB = mk("[2/3]", pen3, FaxB);
    const gov = rB.FvRk < rA.FvRk ? rB : rA;
    return Object.assign({}, gov, { planes: [rA, rB], govLabel: gov.label, thread: th });
  };
  const panelKind1 = panel1 ? panelKindOf(v.mat1Specie, v.mat1Classe) : null;
  const panelFh1 = panel1 && panelKind1 ? panelFhK(panelKind1, d, rhok1, t1) : null;
  let resistenza: JohansenResult,
    fh1k: number | null = null;
  if (materiale1 === "acciaio") {
    resistenza = steelTimberSingleShear(d, t1, t2Johansen, fh2k, myRk, faxRkPerJohansen);
  } else {
    fh1k = panel1
      ? panelFh1
        ? panelFh1.value
        : NaN
      : clt1
        ? clt1.latoInserimento === "wide"
          ? cltWideFhK(d, cltRhokWideFace(clt1), alphaElemento1, predrilled)
          : etaFhKCltNarrow(d)
        : etaFhK(d, rhok1, alphaElemento1, predrilled);
    resistenza = threeD
      ? // double shear (EC5 8.7): t1 = min(thickness of 2, penetration in 3), t2 = central (1); fh1 = outer, fh2 = element 1
        doubleShearByPlane(fh1k)
      : threeS
        ? timberTimberSingleShear(d, tHead, t2Johansen, fh2k, fh1k, myRk, faxRkPerJohansen) // head = element 2, tip = element 1
        : timberTimberSingleShear(d, t1, t2Johansen, fh1k, fh2k, myRk, faxRkPerJohansen);
    resistenza.roles3 = threeS;
    resistenza.double = threeD;
    resistenza.panelFlag = panel1;
    if (resistenza.planes) resistenza.planes.forEach((pl) => (pl.panelFlag = panel1));
  }
  // shear capacity of the connection with n_ef IN SHEAR (two distinct n_ef, as in MY PROJECT); ×2 shear planes with
  // three members (two identical connections in parallel)
  const FvRkPerNef = nefShear * resistenza.FvRk * nPiani;
  const FvRkConnessione = FvRkPerNef;

  // design load and check (kmod, gammaM)
  const classeServizio = pInt(v.classeServizio);
  const durataCarico = v.durataCarico;
  const kmodRow: Record<string, number> = ctx.cat.KMOD[classeServizio] ?? {};
  const kmodWood = kmodRow[durataCarico] as number;
  // panel-timber: kmod = √(kmod,panel · kmod,timber) (EC5 §2.3.2.2(2)); panel kmod from Table 3.1 (null = service
  // class not admitted)
  const kmodPanel = panel1 ? panelKmod(v.mat1Classe, classeServizio, durataCarico) : null;
  const kmod = panel1 ? (kmodPanel != null ? Math.sqrt(kmodWood * kmodPanel) : NaN) : kmodWood;
  let panelIssue: string | null = null;
  if (panel1) {
    const tp = ctx.t.engine.panel;
    const classeP = v.mat1Classe;
    const g = screwRec ? screwRec.geometria_mm : null;
    if (!panelKind1 || PANEL_EXCLUDED_CLASSI.includes(classeP)) {
      panelIssue = tp.notAdmitted(classeP);
    } else if (!panelFh1) {
      panelIssue = tp.noEmbedment(fmtN(d, 1));
    } else if (kmodPanel == null) {
      panelIssue = tp.noKmod(classeP, String(classeServizio));
    } else if (g) {
      const minT = PANEL_MIN_T[panelKind1] as number;
      const tMin = Math.max(minT, 1.2 * d);
      if (Number.isFinite(t1) && t1 < tMin - 1e-9) panelIssue = tp.tooThin(fmtN(t1, 1), minT, fmtN(tMin, 1));
      else if (!(g.dk >= 2 * g.d1 - 1e-9)) panelIssue = tp.smallHead(fmtN(g.dk, 1), fmtN(2 * g.d1, 1));
      else if (!/^(HBS|TBS)\b/i.test(screwRec!.family || "")) panelIssue = tp.onlyHbsTbs;
    }
  }
  const gammaM = v.gammaM;
  // Fv,Rd = kmod·Fv,Rk(connection)/γM (the head pull-through limit on the connection was removed 2026-10-08: it only
  // limits the rope effect and the withdrawal check)
  const FvRdPerNef = (kmod * FvRkConnessione) / gammaM;
  const FvRdUnione = FvRdPerNef;
  const faxRd = (kmod * faxRkEf) / gammaM;
  const FaxRkUnione = faxRkEf * nef * (threeD ? 1 : nPiani); // double shear: one head per screw, not doubled
  const FaxRdUnione = (kmod * FaxRkUnione) / gammaM;
  const fnKN = currentFn(ctx),
    fvKN = currentFv(ctx);
  const faKN = currentFa(ctx);
  const forceF_kN = Math.hypot(fnKN, fvKN);
  const forceF_N = forceF_kN * 1000;
  const eta = FvRdUnione > 0 ? forceF_N / FvRdUnione : Infinity;
  // Combined axial + lateral load, EC5 §8.3.3(1) eq. (8.28): (Fax,Ed/Fax,Rd)² + (Fv,Ed/(n·Fv,Rd))² ≤ 1, n = number
  // of screws (no group reduction), Fv,Rd per screw. Only when Fa > 0.
  const FvRdVite = (kmod * resistenza.FvRk) / gammaM;
  const etaCombinata =
    faKN > 0
      ? Math.pow((faKN * 1000) / FaxRdUnione, 2) + Math.pow(forceF_N / (nPiani * viteAmmissibili * FvRdVite), 2)
      : null;
  // Withdrawal utilisation (linear ratio)
  const etaAxSimple = faKN > 0 ? (FaxRdUnione > 0 ? (faKN * 1000) / FaxRdUnione : Infinity) : null;

  // SPLITTING, EC5 §8.1.4 (8.2)-(8.5) on the CENTRAL member (element 2; element 1 with three members): h = b, b = t,
  // he = from the loaded edge to the farthest screw; Fv,Ed = component perpendicular to the grain.
  const viteAmmissibiliPuntiSplitting = grid.filter((p) => p.ok);
  const edgeSgn = fvKN >= 0 ? 1 : -1;
  const ptsSplit = twoSides ? viteAmmissibiliPuntiSplitting.concat(gridB!.filter((p) => p.ok)) : viteAmmissibiliPuntiSplitting;
  const heElem2 = viteAmmissibiliPuntiSplitting.length
    ? three
      ? edgeSgn > 0
        ? b1 / 2 - Math.min(...ptsSplit.map((p) => p.v))
        : b1 / 2 + Math.max(...ptsSplit.map((p) => p.v))
      : b2 / 2 - Math.min(...ptsSplit.map((p) => p.x))
    : null;
  const splitB = three ? b1 : b2,
    splitT = three ? t1 : t2;
  // EC5 (8.4) is for solid/glulam timber: no splitting check with a CLT central member (user request 2026-10-14)
  const centralClt = !!(three ? clt1 : steel3 ? clt2 : null);
  const F90Rk = heElem2 != null && !(threeD && panel1) && !centralClt ? splittingF90Rk(splitT, splitB, heElem2, undefined) : null;
  const F90Rd = F90Rk != null ? splittingF90Rd(F90Rk, kmod, gammaM) : null;
  const FvEdSplitting = three ? Math.abs(fvKN) * 1000 : forceF_N * Math.sin((alpha * Math.PI) / 180);
  const etaSplitting = F90Rd != null && F90Rd > 0 ? FvEdSplitting / F90Rd : null;

  return {
    theta,
    b1,
    d1,
    b2,
    d2,
    t1,
    t2,
    lines1,
    bounds2,
    grid,
    resistenza,
    nef,
    rows,
    cols,
    FvRkConnessione,
    nefShear,
    nefShearElem1,
    nefShearElem2,
    nefShearGovernElem,
    angleGrainElem1,
    angleGrainElem2,
    s11,
    s12,
    s11Min,
    s12Min,
    spreadActive,
    faxCRk,
    FvRkPerNef,
    FvRdPerNef,
    faxRk,
    faxRkEf,
    faxRd,
    FaxRkUnione,
    FaxRdUnione,
    FvRdVite,
    etaCombinata,
    etaAxSimple,
    heElem2,
    F90Rk,
    F90Rd,
    FvEdSplitting,
    etaSplitting,
    splitB,
    splitT,
    splitMember: three ? 1 : 2,
    three,
    threeD,
    threadD,
    threadFull,
    steel3,
    twoSides,
    tipOverlap,
    staggerOpt,
    tHead,
    tPoint,
    nPiani,
    cltPoint: threeS ? clt1 : clt2,
    gridB,
    stagger,
    staggerDir,
    viteAmmissibiliB: twoSides ? gridB!.filter((p) => p.ok).length : null,
    kser,
    kserBranch,
    kserRhoM,
    rhomean1,
    rhomean2,
    viteAmmissibili,
    viteTotali,
    materiale1,
    panel1,
    panelKind1,
    panelFh1,
    panelIssue,
    kmodWood,
    kmodPanel,
    kmod,
    gammaM,
    FvRdUnione,
    forceF_kN,
    eta,
    fnKN,
    fvKN,
    faKN,
    centroOrizz: centroIn[0],
    centroVert: centroIn[1],
    sfalsaViti,
    d,
    fi1,
    fi2,
    clt1,
    clt2,
    crossDominant,
    screwRec,
    rondelleCompat,
    rondellaSelCodice,
    rondellaInfo,
    rondellaH,
    screwL,
    screwLEff,
    // predrilling imposed by the ETA (report)
    predrillReasons: predrillRequirement(ctx, screwRec ? screwRec.family : v.etaFamiglia).reasons,
    fh1k,
    fh2k,
    alphaElemento1,
    alphaElemento2,
    predrilled,
    rhok1,
    rhok2,
    blocked: null,
  };
}
